import { createContext, useContext, useEffect, ReactNode } from 'react'

/**
 * Brand Modes (cinematic lenses):
 *
 * A - Stealth Luxury: softer glass, less glow, more whitespace, minimal micro-labeling
 *     Used for: Home, Login, Register, Legal pages
 *
 * B - Technical Museum: chips, micro-labels, tighter grids, instrument-panel density
 *     Used for: Dashboard
 */
export type BrandMode = 'A' | 'B' | 'C'

interface BrandModeContextType {
  mode: BrandMode
}

const BrandModeContext = createContext<BrandModeContextType>({ mode: 'A' })

const MODE_TOKENS: Record<BrandMode, Record<string, string>> = {
  A: {
    '--brand-surface-strength': '0.04',
    '--brand-glow-intensity': '0.08',
    '--brand-spacing-density': '1',
    '--brand-grid-gap': '2rem',
    '--brand-label-opacity': '0.35',
  },
  B: {
    '--brand-surface-strength': '0.08',
    '--brand-glow-intensity': '0.15',
    '--brand-spacing-density': '0.85',
    '--brand-grid-gap': '1.25rem',
    '--brand-label-opacity': '0.55',
  },
  C: {
    '--brand-surface-strength': '0.05',
    '--brand-glow-intensity': '0.12',
    '--brand-spacing-density': '1.15',
    '--brand-grid-gap': '2.5rem',
    '--brand-label-opacity': '0.40',
  },
}

interface BrandModeProviderProps {
  mode: BrandMode
  children: ReactNode
}

export function BrandModeProvider({ mode, children }: BrandModeProviderProps) {
  useEffect(() => {
    const root = document.documentElement
    const tokens = MODE_TOKENS[mode]

    root.setAttribute('data-brand-mode', mode)

    for (const [prop, value] of Object.entries(tokens)) {
      root.style.setProperty(prop, value)
    }

    return () => {
      root.removeAttribute('data-brand-mode')
      for (const prop of Object.keys(tokens)) {
        root.style.removeProperty(prop)
      }
    }
  }, [mode])

  return (
    <BrandModeContext.Provider value={{ mode }}>
      {children}
    </BrandModeContext.Provider>
  )
}

export function useBrandMode(): BrandModeContextType {
  return useContext(BrandModeContext)
}
